import { ChartWithNoAxesIcon } from '@/components/icons/chart-with-no-axes-icon'
import { DatabaseIcon } from '@/components/icons/database-icon'
import { Breadcrumb } from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import { DataTableColumnHeader } from '@/components/ui/data-table'
import { Input } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableErrorRow,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeletonRows,
} from '@/components/ui/table'
import { toast } from '@/components/ui/toaster'
import {
  useDataQualScoreCardsQuery,
  useMetaDataScoreCardsQuery,
} from '@/hooks/api/use-datagovernance-dashboard-query'
import {
  TableDetailsQueryParams,
  useTableDetailsQuery,
  type ColumnDetail,
} from '@/hooks/api/use-table-details-query'
import { useTableMetadataVersionHistoryQuery } from '@/hooks/api/use-table-metadata-version-history-query'
import {
  CellContext,
  Column,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { T } from '@tolgee/react'
import { FileCode, FileX, SearchIcon, Target, TrendingUp } from 'lucide-react'
import React from 'react'
import { PageTemplate } from '../_common/page-template'
import {
  EditableFieldName,
  EnrichmentControls,
  MergedColumnData,
  TableViewType,
  useColumnEnrichment,
  useEnrichmentHandlers,
  type EnrichmentControlsState,
} from './_common'
import { DataQualityRuleDialog } from './_common/components/data-quality-rule-dialog'
import { EditableCell, EditableTags } from './_common/components/editable-cell'
import { SensitivityTagsCell } from './_common/components/tables-common-cells'
import { TableMetadataVersionHistory } from './_common/components/version-history'
import { ENRICHMENT_FIELD_MAPPINGS, TAG_CATEGORIES } from './_common/constants'
import {
  useDataCatalogNavigation,
  useMetadataTableBreadcrumbs,
} from './_common/hooks/use-data-catalog-breadcrumbs'
import { useSaveMetadata } from './_common/hooks/use-save-metadata'
import { useEditing } from './_common/hooks/use-table-cell-editing'
import {
  extractDataQualityTags,
  extractPiiTags,
  extractSensitivityTags,
  getEnrichmentFields,
  shouldDisableClearAllButton,
  shouldDisableTagOptions,
} from './_common/utils'
import { ScoreCardsUpdate } from './dashboard/_components/_common/scorecards-update'
import { getStyles } from './dashboard/utils'

type MetaDataTableProps = Readonly<TableDetailsQueryParams>

const MetaDataTable = (props: MetaDataTableProps) => {
  const [tableVersion, setTableVersion] = React.useState('')
  const [tableCurrentView, setTableCurrentView] = React.useState<TableViewType>('metadata')
  const tableVHQuery = useTableMetadataVersionHistoryQuery(props)
  const tableDetailsQuery = useTableDetailsQuery(
    { ...props, tableVersion },
    { enabled: Boolean(tableVersion || (!tableVersion && tableVHQuery.isError)) },
  )
  const metadataScoreQuery = useMetaDataScoreCardsQuery({
    filters: {
      database: props.database_name,
      schema: props.schema_name,
      table: props.table_name,
      sensitivity: 'all',
    },
    queryOptions: {
      enabled: tableCurrentView === 'metadata',
    },
  })

  const dataQualityScore = useDataQualScoreCardsQuery({
    filters: {
      database: props.database_name,
      schema: props.schema_name,
      table: props.table_name,
      sensitivity: 'all',
    },
    queryOptions: {
      enabled: tableCurrentView === 'dataQuality',
    },
  })

  const [sorting, setSorting] = React.useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = React.useState('')
  const { breadcrumbItems } = useMetadataTableBreadcrumbs(
    props.database_name,
    props.schema_name,
    props.table_name,
    props.assetType,
  )
  const { openDataGovernanceDashboardTab } = useDataCatalogNavigation()

  const currentTableVersion = React.useMemo(() => {
    return tableVHQuery.data?.current_version || ''
  }, [tableVHQuery.data?.current_version])

  // Function to refetch score queries
  const refetchScoreQueries = React.useCallback(() => {
    metadataScoreQuery.refetch()
    dataQualityScore.refetch()
  }, [metadataScoreQuery, dataQualityScore])

  const isNoData =
    tableDetailsQuery.isError ||
    tableDetailsQuery.isPending ||
    tableDetailsQuery.data.tables.length === 0

  const [enrichmentState, setEnrichmentState] = React.useState<EnrichmentControlsState>({
    selectedAgent: 'emAgents',
    showEnrichmentControls: false,
  })

  // Data Quality Rule Dialog state
  const [dataQualityDialogOpen, setDataQualityDialogOpen] = React.useState(false)
  const [selectedColumnData, setSelectedColumnData] = React.useState<{
    columnName: string
    dataQualityTags: any[]
  } | null>(null)

  const handleDataQualityDialogOpen = React.useCallback(
    (columnName: string, dataQualityTags: any[]) => {
      setSelectedColumnData({
        columnName,
        dataQualityTags,
      })
      setDataQualityDialogOpen(true)
    },
    [],
  )

  const enrichmentWorkflow = useColumnEnrichment(
    props.table_name,
    tableDetailsQuery.data,
    props.schema_name,
  )

  const columns = tableDetailsQuery.data?.tables[0]?.columns || []
  const tableData = tableDetailsQuery.data?.tables[0]
  const memoizedColumns = React.useMemo(() => {
    return columns
  }, [tableDetailsQuery.data?.tables?.[0]?.columns])

  const taggingFlags = tableData
    ? {
        is_pii_tagged: tableData.is_pii_tagged,
        is_sensitivity_tagged: tableData.is_sensitivity_tagged,
        is_dq_tagged: tableData.is_dq_tagged,
      }
    : undefined

  const isTableVersionChanged = React.useMemo(
    () => !!(tableVersion && currentTableVersion && tableVersion !== currentTableVersion),
    [tableVersion, currentTableVersion],
  )
  const { editingState, editingHandlers, editingRefs } = useEditing(
    memoizedColumns,
    [],
    isTableVersionChanged,
  )
  const { handleTagDelete } = editingHandlers

  // Helper function to update table version from version history
  const updateTableVersionFromHistory = React.useCallback(async () => {
    try {
      const versionHistoryResult = await tableVHQuery.refetch()
      const newCurrentVersion = versionHistoryResult.data?.current_version || ''
      if (newCurrentVersion) {
        const previousVersion = tableVersion
        setTableVersion(newCurrentVersion)

        // refetch table details if version has not changed
        if (previousVersion === newCurrentVersion) {
          await tableDetailsQuery.refetch()
        }
      }
    } catch (error) {
      console.error('Failed to fetch version history:', error)
    }
  }, [tableVHQuery, tableDetailsQuery, tableVersion])

  const saveMetadata = useSaveMetadata({
    queryParams: props,
    context: 'editing',
    refetchScoreQueries,
  })

  // Handle saving editing changes
  const handleSaveEdits = React.useCallback(async () => {
    if (editingState.editMode && Object.keys(editingState.columns).length > 0) {
      const saveData = saveMetadata.convertEditingColumnData(editingState.columns, memoizedColumns)
      const success = await saveMetadata.saveColumnData(saveData, props.table_name)

      if (success) {
        // Check if business fields were edited
        const businessFieldsEdited = saveMetadata.checkBusinessFieldsEdited(
          editingState.columns,
          memoizedColumns,
        )

        if (businessFieldsEdited) {
          toast.warning(
            'Tags are dependent on Business Definitions and Rules. Please enrich Tags if you edit Business Definitions and Rules.',
          )
        }
        await updateTableVersionFromHistory()
        editingHandlers.clearLocalEdits()
      }
    }
  }, [
    editingState.editMode,
    editingState.columns,
    saveMetadata,
    props.table_name,
    editingHandlers,
    memoizedColumns,
  ])

  // Handle clear local edits - also revert to current version
  const handleClearLocalEdits = React.useCallback(() => {
    setTableVersion(currentTableVersion)
    editingHandlers.clearLocalEdits()
  }, [currentTableVersion, editingHandlers])

  const enrichedColumns = React.useMemo((): MergedColumnData[] => {
    if (!enrichmentWorkflow.state.enrichmentData?.tables?.[0]?.columns) {
      return []
    }

    const enrichedCols = enrichmentWorkflow.state.enrichmentData.tables[0].columns
    const originalCols = memoizedColumns

    // Merge enriched data with original data
    return originalCols.map((originalCol) => {
      const enrichedCol = enrichedCols.find(
        (ec: any) => (ec.columnName || ec.column_name) === originalCol.column_name,
      )

      if (enrichedCol) {
        return {
          ...originalCol,
          column_description:
            enrichedCol.columnDescription ||
            enrichedCol.column_description ||
            originalCol.column_description,
          business_definition:
            enrichedCol.businessDefinition ||
            enrichedCol.business_definition ||
            originalCol.business_definition,
          business_rules:
            enrichedCol.businessRules || enrichedCol.business_rules || originalCol.business_rules,
          tags: enrichedCol.tags || originalCol.tags,
          _isEnriched: true,
        }
      }

      return {
        ...originalCol,
        _isEnriched: false,
      }
    })
  }, [enrichmentWorkflow.state.enrichmentData, memoizedColumns])

  const enrichmentFields = getEnrichmentFields(enrichmentWorkflow.state.selectedMetadata || [])
  const shouldDisableClearAll = React.useMemo(
    () => shouldDisableClearAllButton(memoizedColumns),
    [memoizedColumns],
  )
  const shouldDisableTags = React.useMemo(
    () => shouldDisableTagOptions(memoizedColumns),
    [memoizedColumns],
  )
  const handlers = useEnrichmentHandlers(
    'column',
    enrichmentWorkflow,
    tableDetailsQuery,
    setEnrichmentState,
    props,
    enrichmentState,
    updateTableVersionFromHistory,
    refetchScoreQueries,
  )

  // Create all column definitions in the correct order
  const columnDefs = React.useMemo<ColumnDef<ColumnDetail | MergedColumnData>[]>(() => {
    return createAllColumnDefinitions(
      enrichmentWorkflow.isEnrichmentInProcess,
      enrichmentFields,
      enrichmentState.showEnrichmentControls,
      enrichmentWorkflow.isEnrichmentCompleted,
      editingState.editingCell,
      editingState.editingField,
      editingHandlers.handleFieldEdit,
      editingHandlers.handleCellClick,
      editingHandlers.handleTextareaBlur,
      editingRefs.textareaRefs,
      editingState,
      handleTagDelete,
      tableCurrentView,
      handleDataQualityDialogOpen,
    )
  }, [
    enrichmentWorkflow.isEnrichmentInProcess,
    enrichmentFields,
    enrichmentWorkflow.isEnrichmentCompleted,
    editingState.editingCell,
    editingState.editingField,
    editingState.columns,
    handleTagDelete,
    tableCurrentView,
  ])

  const originalTable = useReactTable({
    data: memoizedColumns,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    sortDescFirst: true,
    globalFilterFn: (row, columnId, value) => {
      // filter by the column_name column
      const columnName = row.getValue('column_name') as string
      return columnName.toLowerCase().includes(value.toLowerCase())
    },
    state: {
      sorting,
      globalFilter,
    },
    enableColumnFilters: false,
  })

  const enrichedTable = useReactTable({
    data: enrichedColumns,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    sortDescFirst: true,
    globalFilterFn: (row, columnId, value) => {
      // filter by the column_name column
      const columnName = row.getValue('column_name') as string
      return columnName.toLowerCase().includes(value.toLowerCase())
    },
    state: {
      sorting,
      globalFilter,
    },
    enableColumnFilters: false,
  })

  // Determine which table and data to show
  const showEnrichedDataView = enrichmentWorkflow.isEnrichmentCompleted
  const enrichmentCurrentView = enrichmentWorkflow.state.currentView
  const activeTable =
    showEnrichedDataView && enrichmentCurrentView === 'enriched' ? enrichedTable : originalTable
  const activeRows = activeTable.getRowModel().rows
  const hasNoColumns = memoizedColumns.length === 0

  const isInitialLoading = tableDetailsQuery.isPending
  const isTableLoading = isInitialLoading && hasNoColumns

  React.useEffect(() => {
    if (tableVersion) {
      editingHandlers.clearLocalEdits()
    }
  }, [tableVersion])

  React.useEffect(() => {
    if (isTableVersionChanged && memoizedColumns.length) {
      editingHandlers.handleTableVersionChange()
    }
  }, [isTableVersionChanged, memoizedColumns])

  React.useEffect(() => {
    if (!tableVersion && currentTableVersion) {
      setTableVersion(currentTableVersion)
    }
  }, [tableVersion, currentTableVersion])

  return (
    <div className='flex w-full flex-col gap-6'>
      <PageTemplate.Header hasBreadcrumb className='flex-wrap items-center justify-between gap-4'>
        <div className='space-y-4'>
          <Breadcrumb items={breadcrumbItems} />
          <PageTemplate.H4>
            <T keyName='metadata.subtitle' />
          </PageTemplate.H4>
        </div>
        <div className='flex gap-6'>
          <div className='relative'>
            <SearchIcon className='absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-200' />
            <Input
              placeholder='Search'
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className='w-80 pl-10'
            />
          </div>

          {currentTableVersion && (
            <TableMetadataVersionHistory
              tableVHData={tableVHQuery.data}
              currentTableVersion={tableVHQuery.data?.current_version}
              tableVersion={tableVersion}
              setTableVersion={setTableVersion}
            />
          )}
        </div>
      </PageTemplate.Header>

      {/* Toggle buttons and score buttons */}
      <div className='flex items-center gap-6'>
        <div className='flex w-fit gap-2 rounded-lg bg-gray-100 p-2'>
          <Button
            variant={tableCurrentView === 'metadata' ? 'black' : 'grey'}
            className='flex items-center gap-2 rounded-lg border-none normal-case'
            onClick={() => setTableCurrentView('metadata')}
          >
            <DatabaseIcon />
            <p className='text-sm font-normal'>Metadata View</p>
          </Button>
          <Button
            variant={tableCurrentView === 'dataQuality' ? 'black' : 'grey'}
            className='flex items-center gap-2 rounded-lg border-none normal-case'
            onClick={() => setTableCurrentView('dataQuality')}
          >
            <ChartWithNoAxesIcon className='h-4 w-4' />
            <p className='text-sm font-normal'>Data Quality View</p>
          </Button>
        </div>

        {/* Score buttons */}
        <div>
          {tableCurrentView === 'metadata' && (
            <Button
              unstyled
              className='flex items-center gap-2 rounded-lg p-2 hover:bg-neutral-50 disabled:cursor-not-allowed'
              disabled={!!tableDetailsQuery.data?.error || metadataScoreQuery.isLoading}
              onClick={() => {
                openDataGovernanceDashboardTab(
                  'metaData',
                  props.database_name,
                  props.schema_name,
                  props.table_name,
                  props.assetType,
                  'all',
                )
              }}
            >
              <div className='flex flex-col items-start gap-1'>
                <div className='flex items-center gap-2'>
                  <p className='text-sm font-normal'>Metadata Score</p>
                  <TrendingUp className='size-4 text-neutral-200' />
                </div>
                {metadataScoreQuery.isLoading ? (
                  <div className='mt-2 h-2 w-24 animate-pulse rounded-full bg-gray-200' />
                ) : (
                  <div className='flex items-center gap-2'>
                    <span
                      className={`text-sm font-medium ${getStyles(metadataScoreQuery.data?.score || 0).text}`}
                    >
                      {metadataScoreQuery.data?.score
                        ? `${Math.round(metadataScoreQuery.data.score)}%`
                        : 'N/A'}
                    </span>
                    <Progress
                      value={metadataScoreQuery.data?.score || 0}
                      className='h-1 w-16'
                      color={getStyles(metadataScoreQuery.data?.score || 0).progress}
                    />
                  </div>
                )}
              </div>
            </Button>
          )}
          {tableCurrentView === 'dataQuality' && (
            <Button
              unstyled
              className='flex items-center gap-2 rounded-lg p-2 hover:bg-neutral-50 disabled:cursor-not-allowed'
              disabled={!!tableDetailsQuery.data?.error || dataQualityScore.isLoading}
              onClick={() => {
                openDataGovernanceDashboardTab(
                  'dataQuality',
                  props.database_name,
                  props.schema_name,
                  props.table_name,
                  props.assetType,
                  'all',
                )
              }}
            >
              <div className='flex flex-col items-start gap-1'>
                <div className='flex items-center gap-2'>
                  <p className='text-sm font-normal'>Data Quality Score</p>
                  <Target className='size-4 text-neutral-200' />
                </div>
                {dataQualityScore.isLoading ? (
                  <div className='mt-2 h-2 w-24 animate-pulse rounded-full bg-gray-200' />
                ) : (
                  <div className='flex items-center gap-2'>
                    <span
                      className={`text-sm font-medium ${getStyles(dataQualityScore.data?.overall_assessment.percentage_score || 0).text}`}
                    >
                      {dataQualityScore.data?.overall_assessment.percentage_score
                        ? `${Math.round(dataQualityScore.data?.overall_assessment.percentage_score)}%`
                        : 'N/A'}
                    </span>
                    <Progress
                      value={dataQualityScore.data?.overall_assessment.percentage_score || 0}
                      className='h-1 w-16'
                      color={
                        getStyles(dataQualityScore.data?.overall_assessment.percentage_score || 0)
                          .progress
                      }
                    />
                  </div>
                )}
              </div>
            </Button>
          )}
        </div>
      </div>
      {tableCurrentView === 'metadata' && metadataScoreQuery.isFetching ? (
        <ScoreCardsUpdate message='Updating Metadata Score' />
      ) : (
        <></>
      )}
      {tableCurrentView === 'dataQuality' && dataQualityScore.isFetching ? (
        <ScoreCardsUpdate message='Updating Data Quality Score' />
      ) : (
        <></>
      )}

      <div className='flex justify-between'>
        <EnrichmentControls
          type='column'
          isNoData={isNoData}
          enrichmentState={enrichmentState}
          setEnrichmentState={setEnrichmentState}
          enrichmentWorkflow={enrichmentWorkflow}
          hasEmptyMetadataFields={shouldDisableTags}
          onEnrich={handlers.handleEnrich}
          onCancel={handlers.handleCancel}
          onSave={handlers.handleSave}
          onUndoEnrichment={handlers.handleUndoEnrichment}
          onRedoEnrichment={handlers.handleRedoEnrichment}
          onClearAllMetadata={handlers.handleClearAllMetadata}
          disableClearAllMetadata={shouldDisableClearAll || hasNoColumns}
          isSaving={handlers.isSavingMetadata}
          isClearingMetadata={handlers.isClearingMetadata}
          hasLocalEdits={editingState.editMode}
          onClearLocalEdits={handleClearLocalEdits}
          onSaveEdits={handleSaveEdits}
          isSavingEdits={saveMetadata.isSaving}
          tableCurrentView={tableCurrentView}
          latestUpdate={enrichmentWorkflow.latestUpdate}
          taggingFlags={taggingFlags}
        />
      </div>

      <div className='w-full'>
        <Table>
          <TableHeader>
            {activeTable.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    style={{ width: `${header.getSize()}px` }}
                    data-state={
                      (header.column.getIsSorted() || header.column.getIsFiltered()) && 'selected'
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isTableLoading && <TableSkeletonRows rowCount={3} cellCount={columnDefs.length} />}

            {tableDetailsQuery.isError && <TableErrorRow colSpan={columnDefs.length} />}

            {tableDetailsQuery.isSuccess &&
              activeRows.length > 0 &&
              tableVersion &&
              activeRows.map((row) => {
                // Check if any cell in this row has been edited
                const rowData = row.original as ColumnDetail | MergedColumnData
                const isRowEdited = editingState.columns[rowData.column_name] !== undefined

                return (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    className={`h-[72px] ${isRowEdited ? 'bg-neutral-25' : ''}`}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        style={{ width: `${cell.column.getSize()}px` }}
                        data-state={
                          (cell.column.getIsSorted() || cell.column.getIsFiltered()) && 'selected'
                        }
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                )
              })}

            {tableDetailsQuery.isSuccess && activeRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columnDefs.length} className='h-24 text-center'>
                  {tableDetailsQuery.data?.error ? (
                    <p className='text-sm'>{tableDetailsQuery.data.error}</p>
                  ) : (
                    <T keyName='metadata.messages.noColumns' />
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Data Quality Rule Dialog */}
      {selectedColumnData && (
        <DataQualityRuleDialog
          open={dataQualityDialogOpen}
          onOpenChange={setDataQualityDialogOpen}
          dataQualityTags={selectedColumnData.dataQualityTags}
          columnName={selectedColumnData.columnName}
        />
      )}
    </div>
  )
}

export const createEditableColumn = (
  fieldName: EditableFieldName,
  editingCell: string | null,
  editingField: EditableFieldName | null,
  handleFieldEdit: (columnName: string, fieldName: EditableFieldName, value: string) => void,
  handleCellClick: (columnName: string, fieldName?: EditableFieldName) => void,
  handleTextareaBlur: () => void,
  textareaRefs: React.MutableRefObject<Record<string, HTMLTextAreaElement>>,
  editingState: any,
  isEnrichmentInProcess: boolean = false,
  showEnrichmentControls: boolean = false,
  isEnrichmentCompleted: boolean = false,
  enrichmentFields: string[] = [],
): ColumnDef<ColumnDetail | MergedColumnData> => {
  return {
    size: 400,
    accessorKey: fieldName,
    header: ({ column }) => {
      const getTitleKey = (fieldName: EditableFieldName) => {
        switch (fieldName) {
          case 'column_description':
            return 'metadata.table.headers.description' as const
          case 'business_definition':
            return 'metadata.table.headers.businessDefinition' as const
          case 'business_rules':
            return 'metadata.table.headers.businessRules' as const
          default:
            return 'metadata.table.headers.description' as const
        }
      }

      const titleKey = getTitleKey(fieldName)

      return (
        <DataTableColumnHeader column={column} titleKey={titleKey}>
          <T keyName={titleKey} />
        </DataTableColumnHeader>
      )
    },
    cell: ({ cell }: CellContext<ColumnDetail | MergedColumnData, unknown>) => {
      const value = cell.getValue<string>()
      const columnName = cell.row.original.column_name
      const isEnrichingField = isEnrichmentInProcess && enrichmentFields.includes(fieldName)
      const isEditingDisabled =
        showEnrichmentControls || isEnrichmentCompleted || isEnrichmentInProcess

      if (isEnrichingField) {
        return <Skeleton className='h-5' />
      }

      return (
        <EditableCell
          fieldName={fieldName}
          value={value}
          columnName={columnName}
          isEditing={editingCell === columnName && editingField === fieldName}
          isEditingDisabled={isEditingDisabled}
          onEdit={(columnName, value) => handleFieldEdit(columnName, fieldName, value)}
          onCellClick={handleCellClick}
          onBlur={handleTextareaBlur}
          textareaRefs={textareaRefs}
          editingState={editingState}
        />
      )
    },
  }
}

const createAllColumnDefinitions = (
  isEnrichmentInProcess: boolean,
  enrichmentFields: string[],
  showEnrichmentControls: boolean,
  isEnrichmentCompleted: boolean,
  editingCell: string | null,
  editingField: EditableFieldName | null,
  handleFieldEdit: (columnName: string, fieldName: EditableFieldName, value: string) => void,
  handleCellClick: (columnName: string, fieldName?: EditableFieldName) => void,
  handleTextareaBlur: () => void,
  textareaRefs: React.MutableRefObject<Record<string, HTMLTextAreaElement>>,
  editingState: any,
  handleTagDelete: (columnName: string, tagToDelete: string) => void,
  tableCurrentView: TableViewType,
  handleDataQualityDialogOpen: (columnName: string, dataQualityTags: any[]) => void,
): ColumnDef<ColumnDetail | MergedColumnData>[] => {
  const baseColumns = [
    // 1. Name column
    {
      accessorKey: 'column_name',
      header: ({ column }: { column: Column<ColumnDetail | MergedColumnData> }) => (
        <DataTableColumnHeader column={column} titleKey='metadata.table.headers.columnName'>
          <T keyName='metadata.table.headers.columnName' />
        </DataTableColumnHeader>
      ),
      cell: ({ cell }: CellContext<ColumnDetail | MergedColumnData, unknown>) => {
        const columnName = cell.getValue<string>()
        return (
          <p className='text-sm font-semibold' title={columnName}>
            {columnName || '-'}
          </p>
        )
      },
    },
    // 2. Description column (editable)
    createEditableColumn(
      'column_description',
      editingCell,
      editingField,
      handleFieldEdit,
      handleCellClick,
      handleTextareaBlur,
      textareaRefs,
      editingState,
      isEnrichmentInProcess,
      showEnrichmentControls,
      isEnrichmentCompleted,
      enrichmentFields,
    ),
    // 3. Data type column
    {
      accessorKey: 'data_type',
      header: ({ column }: { column: Column<ColumnDetail | MergedColumnData> }) => (
        <DataTableColumnHeader column={column} titleKey='metadata.table.headers.dataType'>
          <T keyName='metadata.table.headers.dataType' />
        </DataTableColumnHeader>
      ),
      cell: ({ cell }: CellContext<ColumnDetail | MergedColumnData, unknown>) => {
        const type = cell.getValue<string>()
        return <p className='text-sm'>{type}</p>
      },
    },
  ]

  if (tableCurrentView === 'metadata') {
    return [
      ...baseColumns,
      // 4. PII Tags column
      {
        accessorKey: 'pii_tags',
        header: () => <T keyName='metadata.table.headers.piiTags' />,
        cell: ({ cell }: CellContext<ColumnDetail | MergedColumnData, unknown>) => {
          const columnData = cell.row.original
          const columnName = columnData.column_name
          const isEnrichingField =
            isEnrichmentInProcess &&
            enrichmentFields.includes(ENRICHMENT_FIELD_MAPPINGS.PII_TAGS[0])
          const isEditingDisabled =
            showEnrichmentControls || isEnrichmentCompleted || isEnrichmentInProcess

          if (isEnrichingField) {
            return <Skeleton className='h-5' />
          }
          // Merge original tags with edited tags
          const originalTags = columnData.tags
          const editedColumnData = editingState.columns[columnName]
          const mergedTags = editedColumnData?.tags || originalTags
          const piiTags = extractPiiTags(mergedTags)

          return (
            <EditableTags
              columnName={columnName}
              tags={piiTags}
              category={TAG_CATEGORIES.PII_DG}
              editingState={editingState}
              onTagDelete={handleTagDelete}
              isEditingDisabled={isEditingDisabled}
            />
          )
        },
      },
      // 5. Sensitivity Tags column
      {
        accessorKey: 'sensitivity_tags',
        header: () => <T keyName='metadata.table.headers.sensitivityTags' />,
        cell: ({ cell }: CellContext<ColumnDetail | MergedColumnData, unknown>) => {
          const columnData = cell.row.original
          const isEnrichingField =
            isEnrichmentInProcess &&
            enrichmentFields.includes(ENRICHMENT_FIELD_MAPPINGS.SENSITIVITY_TAGS[0])

          if (isEnrichingField) {
            return <Skeleton className='h-5' />
          }

          const sensitivityTags = extractSensitivityTags(columnData.tags)
          return <SensitivityTagsCell sensitivityTags={sensitivityTags} />
        },
      },
      // 6. Business Definition column (editable)
      createEditableColumn(
        'business_definition',
        editingCell,
        editingField,
        handleFieldEdit,
        handleCellClick,
        handleTextareaBlur,
        textareaRefs,
        editingState,
        isEnrichmentInProcess,
        showEnrichmentControls,
        isEnrichmentCompleted,
        enrichmentFields,
      ),
      // 7. Business Rules column (editable)
      createEditableColumn(
        'business_rules',
        editingCell,
        editingField,
        handleFieldEdit,
        handleCellClick,
        handleTextareaBlur,
        textareaRefs,
        editingState,
        isEnrichmentInProcess,
        showEnrichmentControls,
        isEnrichmentCompleted,
        enrichmentFields,
      ),
    ]
  } else {
    // Data Quality View
    return [
      ...baseColumns,
      // 4. Data Quality Tags column
      {
        size: 500,
        accessorKey: 'data_quality_tags',
        header: () => <T keyName='metadata.table.headers.dataQualityTags' />,
        cell: ({ cell }: CellContext<ColumnDetail | MergedColumnData, unknown>) => {
          const columnData = cell.row.original
          const columnName = columnData.column_name
          const isEnrichingField =
            isEnrichmentInProcess &&
            enrichmentFields.includes(ENRICHMENT_FIELD_MAPPINGS.DATA_QUALITY_TAGS[0])
          const isEditingDisabled =
            showEnrichmentControls || isEnrichmentCompleted || isEnrichmentInProcess

          if (isEnrichingField) {
            return <Skeleton className='h-5' />
          }

          // Merge original tags with edited tags
          const originalTags = columnData.tags
          const editedColumnData = editingState.columns[columnName]
          const mergedTags = editedColumnData?.tags || originalTags
          const dataQualityTags = extractDataQualityTags(mergedTags)

          return (
            <EditableTags
              columnName={columnName}
              tags={dataQualityTags}
              category={TAG_CATEGORIES.DATA_QUALITY_TAGS}
              editingState={editingState}
              onTagDelete={handleTagDelete}
              isEditingDisabled={isEditingDisabled}
            />
          )
        },
      },
      // 5. Data Quality Rule column
      {
        accessorKey: 'data_quality_rule',
        header: () => <T keyName='metadata.table.headers.dataQualityRule' />,
        cell: ({ cell }: CellContext<ColumnDetail | MergedColumnData, unknown>) => {
          const columnData = cell.row.original
          const isEnrichingField =
            isEnrichmentInProcess &&
            enrichmentFields.includes(ENRICHMENT_FIELD_MAPPINGS.DQ_RULES[0])

          if (isEnrichingField) {
            return <Skeleton className='h-5' />
          }

          const dataQualityTags =
            columnData.tags?.DATA_QUALITY_TAGS || columnData.tags?.dataQualityTags || []

          const handleFileCodeClick = () => {
            handleDataQualityDialogOpen(columnData.column_name, dataQualityTags)
          }

          // Check if there are any data quality tags with SQL scripts
          const hasDataQualityRules = dataQualityTags.some(
            (tag: any) => tag.dq_sql_script && tag.dq_sql_script.trim() !== '',
          )

          return (
            <div className='cursor-pointer place-self-center' onClick={handleFileCodeClick}>
              {hasDataQualityRules ? (
                <FileCode className='size-5 transition-colors hover:text-neutral-400' />
              ) : (
                <FileX className='size-5 transition-colors hover:text-neutral-400' />
              )}
            </div>
          )
        },
      },
    ]
  }
}

export { MetaDataTable }
